import { useState } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useLanguageContext } from "../../client/contexts/LanguageContext";

/**
 * Testimonials : single quote carousel under the pricing table.
 *
 * Full bleed dark section (#0F1729) consistent with HowItWorks / PricingTable:
 * a light aurora bloom behind a single elevated quote card, navigated manually
 * (arrows, dots, keyboard). No autoplay by design.
 *
 * Copy is bilingual inline ({fr,en}) via useLanguageContext, matching the
 * marketing sections' convention rather than the global translations.ts
 * dictionary. The quotes are anonymous on purpose (no names / avatars).
 *
 * Motion uses framer-motion with a full prefers-reduced-motion path (crossfade
 * collapses to an instant swap; entrance reveal ships static).
 */

const TEAL = "#00C9A7";
const EASE = [0.16, 1, 0.3, 1] as const; // ease-out-expo

type Bilingual = { fr: string; en: string };

const QUOTES: Bilingual[] = [
  {
    fr: "Avant Edgely, je perdais mes comptes sans comprendre pourquoi. En deux semaines d'utilisation, j'ai réalisé que je faisais du revenge trading systématiquement après 17h. Maintenant j'ai une règle simple : je coupe à 17h. Mon compte tient.",
    en: "Before Edgely, I kept blowing accounts without understanding why. Within two weeks I realized I was revenge trading every single time after 5pm. Now I have one simple rule: I stop at 5pm. My account holds.",
  },
  {
    fr: "Le Prop Firm Manager est une révolution. J'ai 4 comptes chez 3 prop firms différentes et je gérais tout sur Excel. Aujourd'hui tout est centralisé, je vois mes drawdowns en temps réel et je n'ai plus de mauvaises surprises en fin de journée.",
    en: "The Prop Firm Manager is a game changer. I run 4 accounts across 3 prop firms and used to manage everything in Excel. Now it's all centralized, I see my drawdowns in real time and I get no nasty surprises at the end of the day.",
  },
  {
    fr: "Ce que j'aime avec Edgely, c'est que ça ressemble à un cockpit de trading. Le dashboard modulaire, les widgets qu'on peut organiser comme on veut… J'ai l'impression de trader avec un vrai outil pro pour la première fois.",
    en: "What I love about Edgely is that it feels like a trading cockpit. The modular dashboard, the widgets you arrange however you want… For the first time I feel like I'm trading with a real pro tool.",
  },
  {
    fr: "J'ai touché mon premier payout sur Apex deux semaines après avoir commencé à utiliser Edgely. Coïncidence ? Je ne crois pas. Le fait de voir mes stats clairement m'a forcé à rester discipliné. C'est exactement ce dont j'avais besoin.",
    en: "I got my first payout on Apex two weeks after I started using Edgely. Coincidence? I don't think so. Seeing my stats clearly forced me to stay disciplined. That's exactly what I needed.",
  },
  {
    fr: "La section Analytics m'a ouvert les yeux sur mes vrais patterns. Je pensais être bon le matin, en réalité mes meilleures sessions sont entre 15h30 et 16h30. J'ai réduit mes horaires de trading de moitié et mes résultats ont doublé.",
    en: "The Analytics section opened my eyes to my real patterns. I thought I was sharp in the morning, but my best sessions are actually between 3:30 and 4:30pm. I cut my trading hours in half and my results doubled.",
  },
  {
    fr: "Edgely est le premier journal de trading que j'utilise vraiment tous les jours. Les autres étaient trop compliqués ou trop basiques. Ici tout est pensé pour les prop traders, on sent que les créateurs tradent eux-mêmes.",
    en: "Edgely is the first trading journal I actually use every day. The others were too complicated or too basic. Here everything is built for prop traders, you can tell the creators trade themselves.",
  },
  {
    fr: "Le mode mentor est parfait pour mon activité de coach. Je suis les performances de mes élèves depuis un seul dashboard. Avant je devais leur demander des screenshots, maintenant j'ai tout en temps réel. Gain de temps énorme.",
    en: "Mentor mode is perfect for my coaching business. I track my students' performance from a single dashboard. I used to ask them for screenshots, now I have everything in real time. A huge time saver.",
  },
];

export default function Testimonials() {
  const { language: lang } = useLanguageContext();
  const reduce = useReducedMotion() ?? false;

  // [index, direction] : direction drives the slide-in side (1 = next, -1 = prev).
  const [[index, dir], setState] = useState<[number, number]>([0, 0]);
  const count = QUOTES.length;

  const go = (next: number, direction: number) =>
    setState([(next + count) % count, direction]);
  const prev = () => go(index - 1, -1);
  const next = () => go(index + 1, 1);

  const slideVariants = {
    enter: (d: number) =>
      reduce
        ? { opacity: 0 }
        : { opacity: 0, x: d > 0 ? 36 : -36 },
    center: { opacity: 1, x: 0 },
    exit: (d: number) =>
      reduce
        ? { opacity: 0 }
        : { opacity: 0, x: d > 0 ? -36 : 36 },
  };

  return (
    <section
      className="relative isolate overflow-hidden mx-[calc(-50vw+50%)] w-screen"
      style={{ backgroundColor: "#0F1729" }}
    >
      {/* Atmosphere : single soft teal bloom behind the card */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(45% 55% at 50% 35%, rgba(0,201,167,0.08), transparent 72%)",
        }}
      />

      <div className="relative mx-auto max-w-[960px] px-4 py-12 sm:px-6 sm:py-[72px]">
        {/* Header */}
        <motion.div
          className="mb-12 text-center"
          initial={reduce ? { opacity: 1 } : { opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <p
            className="font-semibold uppercase"
            style={{
              fontSize: "11px",
              letterSpacing: "0.18em",
              color: TEAL,
              marginBottom: "16px",
            }}
          >
            {lang === "fr" ? "Témoignages" : "Testimonials"}
          </p>
          <h2
            className="mx-auto font-display font-bold text-white"
            style={{
              fontSize: "clamp(24px, 4.5vw, 30px)",
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              maxWidth: "620px",
            }}
          >
            {lang === "fr"
              ? "Ce que disent les traders qui utilisent Edgely"
              : "What traders who use Edgely have to say"}
          </h2>
        </motion.div>

        {/* Carousel */}
        <motion.div
          initial={reduce ? { opacity: 1 } : { opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
        >
          <div
            className="relative overflow-hidden px-6 py-10 sm:px-20 sm:py-[52px]"
            style={{
              backgroundColor: "#0d1526",
              borderRadius: "20px",
              border: "0.5px solid rgba(255,255,255,0.07)",
            }}
          >
            {/* Decorative quotation mark */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute select-none"
              style={{
                top: "28px",
                left: "36px",
                fontSize: "96px",
                lineHeight: 1,
                color: "rgba(255,255,255,0.04)",
                fontFamily: "Georgia, 'Times New Roman', serif",
              }}
            >
              &ldquo;
            </span>

            {/* Quote content (animated swap) */}
            <div className="relative mx-auto flex min-h-[200px] max-w-[640px] flex-col items-center justify-center text-center sm:min-h-[180px]">
              {/* Stars (static, outside the swap so they don't flicker) */}
              <div className="mb-6 flex items-center justify-center" style={{ gap: "5px" }}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={22}
                    style={{ color: TEAL, fill: TEAL }}
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                ))}
              </div>

              <AnimatePresence mode="wait" custom={dir} initial={false}>
                <motion.blockquote
                  key={index}
                  custom={dir}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: reduce ? 0.18 : 0.4, ease: EASE }}
                  style={{
                    fontSize: "15px",
                    lineHeight: 1.75,
                    color: "rgba(255,255,255,0.75)",
                    fontStyle: "italic",
                    textWrap: "pretty",
                  }}
                >
                  {QUOTES[index][lang]}
                </motion.blockquote>
              </AnimatePresence>
            </div>

            {/* Arrows */}
            <CarouselArrow
              side="left"
              onClick={prev}
              label={lang === "fr" ? "Témoignage précédent" : "Previous testimonial"}
            />
            <CarouselArrow
              side="right"
              onClick={next}
              label={lang === "fr" ? "Témoignage suivant" : "Next testimonial"}
            />
          </div>

          {/* Dots */}
          <div className="mt-6 flex items-center justify-center" style={{ gap: "6px" }}>
            {QUOTES.map((_, i) => {
              const active = i === index;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => go(i, i > index ? 1 : -1)}
                  aria-label={
                    lang === "fr"
                      ? `Aller au témoignage ${i + 1}`
                      : `Go to testimonial ${i + 1}`
                  }
                  aria-current={active}
                  style={{
                    height: "6px",
                    width: active ? "20px" : "6px",
                    borderRadius: active ? "3px" : "999px",
                    backgroundColor: active ? TEAL : "rgba(255,255,255,0.15)",
                    transition: "width 0.2s ease, background-color 0.2s ease",
                    cursor: "pointer",
                  }}
                />
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── Carousel navigation arrow ────────────────────────────────────────────── */

function CarouselArrow({
  side,
  onClick,
  label,
}: {
  side: "left" | "right";
  onClick: () => void;
  label: string;
}) {
  const Icon = side === "left" ? ChevronLeft : ChevronRight;

  const onEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    const el = e.currentTarget;
    el.style.backgroundColor = "rgba(0,201,167,0.12)";
    el.style.borderColor = "rgba(0,201,167,0.3)";
    el.style.color = TEAL;
  };
  const onLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    const el = e.currentTarget;
    el.style.backgroundColor = "rgba(255,255,255,0.05)";
    el.style.borderColor = "rgba(255,255,255,0.1)";
    el.style.color = "rgba(255,255,255,0.5)";
  };

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      aria-label={label}
      className="absolute hidden items-center justify-center sm:flex"
      style={{
        top: "50%",
        transform: "translateY(-50%)",
        [side]: "16px",
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        backgroundColor: "rgba(255,255,255,0.05)",
        border: "0.5px solid rgba(255,255,255,0.1)",
        color: "rgba(255,255,255,0.5)",
        transition: "background-color 150ms ease, border-color 150ms ease, color 150ms ease",
        cursor: "pointer",
      }}
    >
      <Icon size={20} aria-hidden="true" />
    </button>
  );
}
